import React from 'react';
import { NotificationModal } from './NotificationModal';

interface WelcomeModalProps {
  user: {
    first_name?: string;
    last_name?: string;
    id?: string;
  };
  type: 'welcome' | 'incomplete';
  onAccept: () => void;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ user, type, onAccept, onClose }) => {
  const isWelcome = type === 'welcome';
  
  return (
    <NotificationModal
      isOpen={true}
      onClose={onClose}
      onConfirm={onAccept}
      title={isWelcome ? "Welcome to Mission: Trimpossible!" : "Complete Your Profile"}
      type={isWelcome ? "info" : "warning"}
      size="medium"
      showConfirmButton={true}
      showCancelButton={true}
      confirmButtonText="Complete Profile"
      cancelButtonText="Skip for Now"
      preventBackdropClose={false}
    >
      <>
        <div className="welcome-greeting">
          <div className="welcome-emoji">🎯</div>
          <div className="greeting-text">
            <p><strong>Hi {user.first_name || 'there'}!</strong></p>
            <p className="welcome-subtitle">
              {isWelcome 
                ? "Your account is now active and ready to go."
                : "Let's finish setting up your profile."
              }
            </p>
          </div>
        </div>

        <div className="profile-requirements">
          <h4>What we'll collect:</h4>
          <div className="requirements-grid">
            <div className="requirement-item">
              <div className="requirement-icon">👤</div>
              <div className="requirement-text">
                <strong>Personal Details</strong>
                <span>Date of birth, gender, height, weight</span>
              </div>
            </div>
            <div className="requirement-item">
              <div className="requirement-icon">📍</div>
              <div className="requirement-text">
                <strong>Contact Info</strong>
                <span>Address, phone, timezone</span>
              </div>
            </div>
            <div className="requirement-item optional">
              <div className="requirement-icon">📝</div>
              <div className="requirement-text">
                <strong>Optional</strong>
                <span>Occupation, how you heard about us</span>
              </div>
            </div>
          </div>
        </div>

        <div className="privacy-assurance">
          <div className="privacy-icon">🔒</div>
          <div className="privacy-text">
            <strong>Your privacy matters</strong>
            <p>All information is securely encrypted and used only to personalize your health coaching experience.</p>
          </div>
        </div>
      </>
      
      <style>{`
        .welcome-greeting {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .welcome-emoji {
          font-size: 42px;
          line-height: 1;
          flex-shrink: 0;
        }

        .greeting-text {
          flex: 1;
        }

        .greeting-text p {
          margin: 0 0 8px 0;
          font-size: 16px;
          line-height: 1.4;
        }

        .greeting-text p:last-child {
          margin-bottom: 0;
        }

        .welcome-subtitle {
          color: var(--text-secondary);
          margin: 0;
        }

        .profile-requirements {
          margin-bottom: 24px;
        }

        .profile-requirements h4 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 16px;
        }

        .requirements-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
          margin-bottom: 8px;
        }

        .requirement-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          transition: all 0.2s ease;
        }

        .requirement-item:hover {
          background: var(--bg-tertiary);
          border-color: var(--accent-blue);
        }

        .requirement-item.optional {
          opacity: 0.8;
        }

        .requirement-icon {
          font-size: 18px;
          line-height: 1;
          margin-top: 1px;
        }

        .requirement-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .requirement-text strong {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .requirement-text span {
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.3;
        }

        .privacy-assurance {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px;
          background: rgba(52, 199, 89, 0.05);
          border: 1px solid rgba(52, 199, 89, 0.2);
          border-radius: var(--radius-medium);
        }

        .privacy-icon {
          font-size: 18px;
          line-height: 1;
          margin-top: 1px;
        }

        .privacy-text {
          flex: 1;
        }

        .privacy-text strong {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--accent-green);
          margin-bottom: 3px;
        }

        .privacy-text p {
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.3;
          margin: 0;
        }

        @media (max-width: 768px) {
          .requirements-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
        }

        @media (max-width: 480px) {
          .welcome-greeting {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .welcome-emoji {
            font-size: 40px;
          }

          .greeting-text h3 {
            font-size: 20px;
          }

          .requirement-item {
            padding: 12px;
          }

          .requirements-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }
        }
      `}</style>
    </NotificationModal>
  );
};

export default WelcomeModal;